import alt from 'components/Dispatcher';
import immutableStore from 'utils/immutableStore';
import RepProfilePageActions, {TrendData} from 'actions/RepProfilePageActions';
import RepProfileCallActivity, {TeamCallActivity} from 'records/RepProfileCallActivity';
import RepProfileCoachingActivity, {TeamCoachingActivity} from 'records/RepProfileCoachingActivity';
import {List, Map, Record} from 'immutable';
import RepProfilePageCoachingPlan, {
  RepProfilePageContentDetailScore,
} from 'records/RepProfilePageCoachingPlan';
import zip from 'lodash/zip';

const INITIAL_SELECTED_SCORE_BY_TYPE = 'MY_SCORES';

const State = Record({
  callActivity: new RepProfileCallActivity(),
  callDonut: new List(),
  callTeamMetrics: new TeamCallActivity(),
  callCardCallDetails: null,
  callDetails: new Map(),
  callDetailsFailed: new Map(),
  callDetailsLoading: new Map(),

  // `fields` array returned by call /search endpoint
  // it functions as some type of schema for the calls
  // and is only here because it is used by the CallDetailsDialog
  callFields: null,

  coachingActivity: new RepProfileCoachingActivity(),
  coachingTeamMetrics: new TeamCoachingActivity(),

  teamConversationTrend: new List(),
  teamCallDurationTrend: new List(),

  coachingPlans: new List(),

  coachingReceivedFrom: null,

  didCallActivityFetchFail: false,
  didCallDonutFetchFail: false,
  didCallTeamMetricsFetchFail: false,
  didCoachingPlansFetchFail: false,
  didCoachingActivityFetchFail: false,
  didCoachingTeamMetricsFetchFail: false,
  didTeamConversationTrendFail: false,
  didTeamCallDurationTrendFail: false,
  didSummaryFetchFail: false,

  isCallActivityFetching: false,
  isCallDonutFetching: false,
  isCallTeamMetricsFetching: false,
  isCoachingActivityFetching: false,
  isCoachingTeamMetricsFetching: false,
  isTeamConversationTrendFetching: false,
  isTeamCallDurationTrendFetching: false,
  areCoachingPlansFetching: false,
  isSummaryFetching: false,

  talkListen: List(),
  talkListenLoading: false,
  talkListenError: false,
  interchanges: List(),
  interchangesLoading: false,
  interchangesError: false,

  openSummaries: new Map(),
  summary: undefined,
  // Has selectScoredByType been called yet?
  // Used to ensure that our default view selection logic (show scored by manager if any scores are available, scored
  // by all otherwise) doesn't get applied afterwards.
  scoredByTypeWasSelected: false,
  selectedTabIndex: 0,
  selectedScoredByType: INITIAL_SELECTED_SCORE_BY_TYPE,
  trendData: null,
  trendDataLoading: true,
});

// XXX Refactor this away, we should only need 1 coaching plan model
// find another way to get this data where its needed.
function mapAndFilterCoachingPlans(plans, velocities) {
  // filter out plans that were adhoc/livecoaching but never actually finished.
  const filteredPlans = plans ? plans.filter(p => !(!p.completed && p.unscheduled())) : List();

  const idToPlan = new Map(filteredPlans.map(plan => [plan.id, plan]));

  velocities = velocities ? velocities : [];

  return List(
    velocities.map(velocity => {
      const plan = idToPlan.get(velocity.planId);

      if (!plan) {
        console.warn('filtered out unfinished unscheduled plan ' + velocity.planId);
        return null;
      }

      const scores = plan.contentDetails.map(cd => {
        return new RepProfilePageContentDetailScore({
          planId: plan.id,
          contentDetailId: cd.id,
          skillName: cd.skillName || cd.custom,
          startScore: velocity.firstScore,
          endScore: velocity.lastScore,
        });
      });

      return new RepProfilePageCoachingPlan({
        id: velocity.planId,
        name: plan.name,
        contentDetails: plan.contentDetails,
        coach: plan.coach,
        recipient: plan.recipient,
        start: plan.created,
        end: velocity.planProgress.projectedEnd,
        completed: plan.completed,
        reviews: plan.reviews,
        reviewScores: scores,
        livecoaching: plan.livecoaching,
        adHoc: plan.adHoc,
      });
    })
  ).filter(i => !!i);
}

// TODO separate into different stores for UI state and
// data storage state
@immutableStore
class RepProfilePageStore {
  constructor() {
    this.state = new State();
    this.bindActions(RepProfilePageActions);
  }
  getSummary() {
    this.setState({
      summary: undefined,
      isSummaryFetching: true,
      didSummaryFetchFail: false,
    });
  }
  resetRepData() {
    this.setState({
      callActivity: new RepProfileCallActivity(),
      callDonut: List(),
      callTeamMetrics: new TeamCallActivity(),
      callCardCallDetails: null,
      callDetails: Map(),
      summary: undefined,
      openSummaries: Map(),
      callDetailsFailed: Map(),
      callDetailsLoading: Map(),
      coachingActivity: new RepProfileCoachingActivity(),
      coachingTeamMetrics: new TeamCoachingActivity(),

      teamConversationTrend: List(),
      teamCallDurationTrend: List(),

      coachingPlans: List(),

      talkListen: List(),
      interchanges: List(),
    });
  }
  selectTabIndex(selectedTabIndex) {
    this.setState({
      selectedTabIndex,
    });
  }
  selectScoredByType(selectedScoredByType) {
    this.setState({
      scoredByTypeWasSelected: true,
      selectedScoredByType,
    });
  }
  getSummarySuccess(summary) {
    this.setState({
      summary: summary,
      isSummaryFetching: false,
    });
  }
  getSummaryError(error) {
    console.warn(error);
    this.setState({
      isSummaryFetching: false,
      didSummaryFetchFail: true,
    });
  }
  getCallActivity() {
    this.setState({
      isCallActivityFetching: true,
      didCallActivityFetchFail: false,
    });
  }
  getCallActivitySuccess(callActivity) {
    this.setState({
      callActivity,
      isCallActivityFetching: false,
    });
  }
  getCallActivityError(error) {
    console.warn(error);
    this.setState({
      isCallActivityFetching: false,
      didCallActivityFetchFail: true,
    });
  }
  getCallDonut() {
    this.setState({
      isCallDonutFetching: true,
      didCallDonutFetchFail: false,
    });
  }
  getCallDonutSuccess(callDonut) {
    this.setState({
      callDonut,
      isCallDonutFetching: false,
      didCallDonutFetchFail: false,
    });
  }
  getCallDonutError(error) {
    console.warn(error);
    this.setState({
      isCallDonutFetching: false,
      didCallDonutFetchFail: true,
    });
  }
  getCallTeamMetrics() {
    this.setState({
      isCallTeamMetricsFetching: true,
      didCallTeamMetricsFetchFail: false,
    });
  }
  getCallTeamMetricsSuccess(callTeamMetrics) {
    this.setState({
      callTeamMetrics,
      isCallTeamMetricsFetching: false,
      didCallTeamMetricsFetchFail: false,
    });
  }
  getCallTeamMetricsError(error) {
    console.warn(error);
    this.setState({
      isCallTeamMetricsFetching: false,
      didCallTeamMetricsFetchFail: true,
    });
  }
  getTeamConversationTrend() {
    this.setState({
      isTeamConversationTrendFetching: true,
      didTeamConversationTrendFail: false,
    });
  }
  getTeamConversationTrendSuccess(teamConversationTrend) {
    this.setState({
      teamConversationTrend,
      isTeamConversationTrendFetching: false,
    });
  }
  getTeamConversationTrendError(error) {
    console.warn(error);
    this.setState({
      isTeamConversationTrendFetching: false,
      didTeamConversationTrendFail: true,
    });
  }
  getTeamCallDurationTrend() {
    this.setState({
      isTeamCallDurationTrendFetching: true,
      didTeamCallDurationTrendFail: false,
    });
  }
  getTeamCallDurationTrendSuccess(teamCallDurationTrend) {
    this.setState({
      teamCallDurationTrend,
      isTeamCallDurationTrendFetching: false,
    });
  }
  getTeamCallDurationTrendError(error) {
    console.warn(error);
    this.setState({
      isTeamCallDurationTrendFetching: false,
      didTeamCallDurationTrendFail: true,
    });
  }
  getCoachingActivity(direction) {
    this.setState({
      coachingReceivedFrom: direction,
      isCoachingActivityFetching: true,
      didCoachingActivityFetchFail: false,
    });
  }
  getCoachingActivitySuccess(coachingActivity) {
    this.setState({
      coachingActivity,
      isCoachingActivityFetching: false,
    });
  }
  getCoachingActivityError(error) {
    console.warn(error);
    this.setState({
      isCoachingActivityFetching: false,
      didCoachingActivityFetchFail: true,
    });
  }
  getCoachingTeamMetrics() {
    this.setState({
      isCoachingTeamMetricsFetching: true,
      didCoachingTeamMetricsFetchFail: false,
    });
  }
  getCoachingTeamMetricsSuccess(coachingTeamMetrics) {
    this.setState({
      coachingTeamMetrics,
      isCoachingTeamMetricsFetching: false,
    });
  }
  getCoachingTeamMetricsError(error) {
    console.warn(error);
    this.setState({
      isCoachingTeamMetricsFetching: false,
      didCoachingTeamMetricsFetchFail: true,
    });
  }
  getCoachingPlans(userId) {
    this.setState({
      areCoachingPlansFetching: true,
      coachingPlans: List(),
      didCoachingPlansFetchFail: false,
    });
  }
  getCoachingPlansSuccess([userid, plans, velocities]) {
    this.setState({
      areCoachingPlansFetching: false,
      coachingPlans: mapAndFilterCoachingPlans(plans, velocities),
    });
  }
  getCoachingPlansError([userId, error]) {
    this.setState({
      areCoachingPlansFetching: false,
      didCoachingPlansFetchFail: true,
    });
  }
  getCoachingReceivedActivity(coachingReceivedFrom) {
    this.setState({
      coachingReceivedFrom,
      isCoachingReceivedActivityFetching: true,
      coachingReceivedActivityAvailable: true,
      didCoachingReceivedActivityFetchFail: false,
    });
  }
  getCoachingReceivedActivitySuccess(coachingReceivedActivity) {
    this.setState({
      coachingReceivedActivity,
      coachingReceivedActivityAvailable: !!coachingReceivedActivity,
      isCoachingReceivedActivityFetching: false,
    });
  }
  getCoachingReceivedActivityError(error) {
    this.setState({
      isCoachingReceivedActivityFetching: false,
      didCoachingReceivedActivityFetchFail: true,
    });
  }

  getCallMetrics() {
    this.setState({
      interchangesLoading: true,
      talkListenLoading: true,
      interchangesError: false,
      talkListenError: false,
    });
  }

  getCallMetricsSuccess([interchanges, talkListen]) {
    this.setState({
      interchanges: Map((interchanges || []).map(s => [s.id, s.value])),
      talkListen: Map((talkListen || []).map(s => [s.id, s.value])),
      interchangesLoading: false,
      talkListenLoading: false,
    });
  }

  getCallMetricsError(error) {
    console.warn(error);
    this.setState({
      interchangesLoading: false,
      talkListenLoading: false,
      interchangesError: true,
      talkListenError: true,
    });
  }

  openCallDetails([userId, day]) {
    const opened = !this.state.openSummaries.get(day);
    this.setState({
      // This was changed to an accordion (only one open at once)
      // I left this with a map implementation in case we ever
      // decide to allow multiple to be open
      openSummaries: new Map().set(day, opened),
    });
  }
  getCallDetails([userId, day]) {
    this.setState({
      callDetails: this.state.callDetails.set(day, null),
      callDetailsLoading: this.state.callDetailsLoading.set(day, true),
      callDetailsFailed: this.state.callDetailsFailed.set(day, false),
    });
  }
  getCallDetailsSuccess([userId, day, details, callFields]) {
    this.setState({
      callFields,
      callDetails: this.state.callDetails.set(day, details),
      callDetailsLoading: this.state.callDetailsLoading.set(day, false),
    });
  }
  getCallDetailsError([userId, day, error]) {
    this.setState({
      callDetailsLoading: this.state.callDetailsLoading.set(day, false),
      callDetailsFailed: this.state.callDetailsFailed.set(day, true),
    });
  }
  getTrendData() {
    this.setState({trendDataLoading: true});
  }
  getTrendDataSuccess(trendData) {
    this.setState({trendDataLoading: false, trendData});
  }
  getTopicPercentageTrend() {
    this.setState({trendDataLoading: true});
  }
  getTopicPercentageTrendSuccess([topicTrend, totalCallTrend]) {
    const data = zip(topicTrend.data, totalCallTrend.data).map(([topic, totalCalls]) => {
      const date = topic.date;
      if (totalCalls.value === null || totalCalls.value === 0) {
        return {value: null, date};
      }

      if (topic.value === null) {
        return {value: 0, date};
      }

      const value = (topic.value / totalCalls.value) * 100;
      return {value, date};
    });

    const trendData = new TrendData({data});
    this.setState({trendDataLoading: false, trendData});
  }
  clearTrendData() {
    this.setState({trendData: null});
  }
}

export default alt.createStore(RepProfilePageStore);
